import { isLicensePlateLookup } from '../licensePlate/licensePlate';

export type PersonQueryKind = 'email' | 'phone' | 'name';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9][\d\s-]{5,}$/;
// Norwegian numbers have 8 digits. Fewer is a booking, order, or other record id, which must keep the generic search.
const MIN_PHONE_DIGITS = 8;
// Letters, with an optional hyphen or apostrophe between parts ("Anne-Marie", "O'Brien").
const NAME_WORD = /^[\p{L}]+(?:['’.-][\p{L}]+)*$/u;

// Email, phone, or a person's name. A plate stays a plate. Undefined keeps the generic search,
// including an address that contains a number.
export const personQueryKind = (query: string): PersonQueryKind | undefined => {
  const text = query.trim();
  if (!text || isLicensePlateLookup(text)) {
    return undefined;
  }
  if (EMAIL.test(text)) {
    return 'email';
  }
  if (PHONE.test(text) && text.replace(/\D/g, '').length >= MIN_PHONE_DIGITS) {
    return 'phone';
  }
  const words = text.split(/\s+/);
  const nameShaped =
    words.every((word) => NAME_WORD.test(word)) && words.some((word) => word.replace(/[^\p{L}]/gu, '').length >= 3);
  return nameShaped ? 'name' : undefined;
};

// Searchable attributes that identify a person. Names differ by index (bookings use `user_email`,
// users use `email`, orders use `user_emails`). Asking an index for an attribute it does not
// have fails the whole multi-search, so each model lists only its own.
const customerIdentity: Record<PersonQueryKind, readonly string[]> = {
  email: ['user_emails'],
  phone: ['user_phones', 'user_phones_national'],
  name: ['user_group_names', 'user_first_names', 'user_last_names']
};

const carIdentity: Record<PersonQueryKind, readonly string[]> = {
  email: ['owner_emails', 'car_manager_emails'],
  phone: ['owner_phones', 'owner_phones_national', 'car_manager_phones', 'car_manager_phones_national'],
  name: [
    'user_group_names',
    'owner_first_names',
    'owner_last_names',
    'car_manager_first_names',
    'car_manager_last_names'
  ]
};

const PERSON_ATTRIBUTES: Record<string, Record<PersonQueryKind, readonly string[]>> = {
  core_user_group: {
    email: ['user_emails'],
    phone: ['user_phones', 'user_phones_national'],
    name: ['name', 'user_first_names', 'user_last_names']
  },
  core_user: {
    email: ['email'],
    phone: ['phone_number', 'phone_number_national'],
    name: ['first_name', 'last_name']
  },
  core_booking: {
    email: ['user_email', 'member_emails'],
    phone: ['user_phone', 'user_phone_national', 'member_phones', 'member_phones_national'],
    name: ['user_group_name', 'user_first_name', 'user_last_name', 'member_first_names', 'member_last_names']
  },
  core_order: customerIdentity,
  core_ticket: customerIdentity,
  core_task: customerIdentity,
  core_tire_quote: customerIdentity,
  core_tire_hotel: customerIdentity,
  core_storage_transfer_order: customerIdentity,
  core_inventory_order: {
    email: customerIdentity.email,
    phone: customerIdentity.phone,
    name: [...customerIdentity.name, 'customer_group_name']
  },
  core_route: {
    email: customerIdentity.email,
    phone: customerIdentity.phone,
    name: [...customerIdentity.name, 'worker_first_names', 'worker_last_names']
  },
  core_car: carIdentity,
  core_car_wheel_set: carIdentity
};

export const personSearchAttributes = (model: string, kind: PersonQueryKind): readonly string[] =>
  PERSON_ATTRIBUTES[model]?.[kind] ?? [];
